"use client";

import { ErrorMessage } from "@hookform/error-message";
import { Label } from "radix-ui";
import { type PropsWithRef, forwardRef, useId } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import Select, { type Props as SelectProps, type MultiValue, type ActionMeta } from "react-select";
import { FormErrorMessage, cn } from "../../..";

interface Option {
	label: string;
	value: string;
}

interface MultiSelectProps<T> extends Omit<PropsWithRef<SelectProps<T>>, "isMulti" | "onChange"> {
	label?: string;
	onChange: ((newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>) => void) | undefined;
	wrapperClassName?: string;
}

export const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps<Option>>(
	function MultiSelectRef(props, ref) {
		const { label, onChange, wrapperClassName, ...rest } = props;

		const intl = useIntl();
		const methods = useFormContext();

		const id = useId();
		const errorId = `${id}-error`;
		const hasError = props.name ? methods?.getFieldState(props.name)?.invalid : false;

		return (
			<div ref={ref} className={cn("flex flex-col gap-1", wrapperClassName)}>
				<Label.Root htmlFor={id} className="self-start font-bold">
					{label}
				</Label.Root>

				<Select<Option, true>
					id={id}
					isMulti
					onChange={onChange}
					loadingMessage={() => {
						return intl.formatMessage({
							defaultMessage: "Resultaten worden geladen",
							id: "HP3y9A",
						});
					}}
					noOptionsMessage={() => {
						return intl.formatMessage({ defaultMessage: "Geen resultaten", id: "H69QLg" });
					}}
					placeholder={intl.formatMessage({ defaultMessage: "Kies een", id: "JGXDWf" })}
					classNames={{
						valueContainer: () => "!py-1 !px-3",
						placeholder: (props) => (props.isDisabled ? "!text-shades-dark" : "!text-dark"),
					}}
					aria-errormessage={errorId}
					aria-invalid={hasError}
					{...rest}
				/>

				{hasError && !!props.name && (
					<FormErrorMessage variant="danger" id={errorId}>
						<ErrorMessage name={props.name} />
					</FormErrorMessage>
				)}
			</div>
		);
	},
);
